import { FC, useState, useCallback, ChangeEventHandler, ReactNode } from 'react';
import { useRelayEnvironment } from 'react-relay';
import { defineMessages, useIntl } from 'dibs-react-intl';

import { AddressAutocomplete } from 'dibs-elements/exports/AddressAutocomplete';
import { Spinner } from 'dibs-elements/exports/Spinner';
import useAddressAutocomplete, {
    Suggestion,
    AddressDetails,
} from 'dibs-address-form/exports/useAddressAutocomplete';

import fieldNames from '../../data/sellerApplicationFieldNames.json';

type Props = {
    value: string;
    errorMessage: ReactNode;
    onChange: ChangeEventHandler<HTMLInputElement>;
    onSelect: (value: string) => void;
    onDetailsReceived: (details: AddressDetails) => void;
    disabled?: boolean;
};

export type { AddressDetails };

const messages = defineMessages({
    streetAddress: {
        id: 'DealerApplicationForm.StreetAddressAutocomplete.placeholder',
        defaultMessage: 'Street Address',
    },
});

const StreetAddressAutocomplete: FC<Props> = ({
    value,
    onChange,
    onSelect,
    onDetailsReceived,
    disabled,
    errorMessage,
}) => {
    const intl = useIntl();
    const environment = useRelayEnvironment();
    const [isLoading, setIsLoading] = useState(false);

    const { suggestions, getAddressDetails } = useAddressAutocomplete({
        input: value,
        environment,
        placeType: 'geocode',
    });

    const handleSelect = useCallback(
        async (selected?: Suggestion | null): Promise<void> => {
            const { addressLine } = selected || {};
            onSelect(addressLine || '');
            setIsLoading(true);

            const addressDetails = await getAddressDetails(selected ?? null);

            const { country, city, stateOrRegion, zipCode } = addressDetails || {};

            setIsLoading(false);

            onDetailsReceived({
                country: country || '',
                city: city || '',
                stateOrRegion: stateOrRegion || '',
                zipCode: zipCode || '',
            });
        },
        [getAddressDetails, onDetailsReceived, onSelect]
    );

    return (
        <AddressAutocomplete<Suggestion>
            dataTn="address-input-autocomplete"
            name={fieldNames.dealerInfo.addressLine1}
            options={suggestions || []}
            onSelect={handleSelect}
            rightDecorator={isLoading && <Spinner size="tiny" />}
            value={value}
            onChange={onChange}
            placeholder={intl.formatMessage(messages.streetAddress)}
            disabled={disabled}
            errorMessage={errorMessage}
        />
    );
};

export default StreetAddressAutocomplete;
